import _find from 'lodash/find';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { isNumber } from 'lodash';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

export const isJsonString = (str) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

export const determineSource = (input) => {
  if (input?.startsWith('usr-') || input?.startsWith('usr')) {
    return "users";
  } else if (input?.startsWith('grp-') || input?.startsWith('grp')) {
    return "groups";
  } else {
    return "tickets";
  }
};

export const formatFileSize = (bytes) => {
  if (!isNumber(bytes)) return bytes;
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = bytes / Math.pow(k, i);
  return `${size.toFixed(1)} ${sizes[i]}`;
};

export const formatDateRange = (startDate, endDate, years) => {
  const options = { month: 'short', day: '2-digit' }; // `day: '2-digit'` adds leading zeros
  const start = new Date(startDate);
  const end = new Date(endDate);

  const startFormatted = new Intl.DateTimeFormat('en-US', options).format(start);
  const endFormatted = new Intl.DateTimeFormat('en-US', options).format(end);

  const year = end.getFullYear(); // End date determines the year
  const startyear = start.getFullYear(); // End date determines the year
  return `${years ? startFormatted + ', ' + startyear : startFormatted} - ${endFormatted}, ${year}`;
};

export const mergeAndDeduplicate = (data) => {
  const combinedArray = [...(data.managers || []), ...(data.leads || []), ...(data.teamMembers || [])];
  const uniqueUsers = new Map();
  combinedArray.forEach((user) => {
    if (user && user.userId) {
      uniqueUsers.set(user.userId, user);
    }
  });
  return Array.from(uniqueUsers.values());
};

export const getQueryParams = (url) => {
  // Extract the query string from the URL
  const queryString = url.split('?')[1];

  // Create an object to hold the query parameters and the URL
  const params = {
    url: url.split('?')[0] // store the base URL without query parameters
  };

  // Split the query string into individual key-value pairs
  const pairs = queryString.split('&');

  // Iterate over each pair
  pairs.forEach(pair => {
    const [key, value] = pair.split('=');

    // Decode the URL-encoded key and value and add them to the params object
    params[decodeURIComponent(key)] = decodeURIComponent(value);
  });

  return params;
};

export const camelize = (str) => {
  return str.replace(/(?:^\w|[A-Z]|\b\w)/g, function(word, index) {
    return index === 0 ? word.toLowerCase() : word.toUpperCase();
  }).replace(/\s+/g, '');
};


export const getCompanyLogo = (dataClients, id) => {
  let name = 'Name not available';
  let client = _find(dataClients, function(cli) {
    return cli.customerId === id;
  });
  if (client?.logo) {
    name = client?.logo[0]?.url;
  }

  return name;
};



export const getCompanyName = (dataClients, id) => {
  let name = 'Name not available';
  let client = _find(dataClients, function(cli) {
    return cli.customerId === id;
  });
  name = client?.companyName;
  return name;
};

export const deepMerge = (target, source) => {
  if (!target || typeof target !== 'object') {
    target = {};
  }

  for (const key in source) {
    if (source[key] instanceof Object && target[key] && typeof target[key] === 'object') {
      target[key] = deepMerge(target[key], source[key]);
    } else {
      target[key] = source[key];
    }
  }
  return target;
};


export const getTimeStamp = (timestamp, timezone = Intl.DateTimeFormat().resolvedOptions().timeZone) => {
  const date = dayjs.utc(timestamp).tz(timezone);
  const now = dayjs().tz(timezone);

  if (now.diff(date, 'day') === 1) {
    return 'yesterday';
  } else if (now.diff(date, 'day') > 1) {
    return date.format('DD/MM/YYYY');
  } else {
    return date.format('h:mm A');
  }
};



export const getDrivePermission = (sharedWith, currentUserId, createdBy) => {
  if (createdBy === currentUserId) return { canEdit: true, canComment: true, canView: true };
  const userPermission = sharedWith?.users?.find(user => user.userId === currentUserId)?.permission;
  switch (userPermission) {
  case 1: // Editor
    return { canEdit: true, canComment: true, canView: true };
  case 2: // Commenter
    return { canEdit: false, canComment: true, canView: true };
  case 0: // Viewer
    return { canEdit: false, canComment: false, canView: true };
  default:
    return { canEdit: false, canComment: false, canView: false };
  }
};


export const getDateRange = (key, includeTime = false) => {
  const today = new Date();
  const startOfWeek = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - today.getUTCDay()));

  const startOfLastWeek = new Date(startOfWeek);
  startOfLastWeek.setUTCDate(startOfLastWeek.getUTCDate() - 7);

  const startOfMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
  const startOfLastMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1));
  const endOfLastMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 0));

  const startOfYear = new Date(Date.UTC(today.getUTCFullYear(), 0, 1));
  const startOfLastYear = new Date(Date.UTC(today.getUTCFullYear() - 1, 0, 1));
  const endOfLastYear = new Date(Date.UTC(today.getUTCFullYear() - 1, 11, 31));

  const startOfLast3Months = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 3, 1));
  const startOfLast6Months = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 6, 1));

  const formatDate = (date) => includeTime ? date.toISOString() : date.toISOString().split('T')[0];

  const dateRanges = {
    lastWeek: { startDate: formatDate(startOfLastWeek), endDate: formatDate(new Date(startOfWeek - 1)) },
    currentWeek: { startDate: formatDate(startOfWeek), endDate: formatDate(today) },
    lastMonth: { startDate: formatDate(startOfLastMonth), endDate: formatDate(endOfLastMonth) },
    currentMonth: { startDate: formatDate(startOfMonth), endDate: formatDate(today) },
    last3Months: { startDate: formatDate(startOfLast3Months), endDate: formatDate(today) },
    last6Months: { startDate: formatDate(startOfLast6Months), endDate: formatDate(today) },
    currentYear: { startDate: formatDate(startOfYear), endDate: formatDate(today) },
    lastYear: { startDate: formatDate(startOfLastYear), endDate: formatDate(endOfLastYear) },
    last2Years: {
      startDate: formatDate(new Date(Date.UTC(today.getUTCFullYear() - 2, 0, 1))),
      endDate: formatDate(today)
    },
    alltime: { startDate: '2023-01-01', endDate: formatDate(today) }
  };

  return dateRanges[key];
};



export const generateKey = (name) => {
  const nameWords = name.trim().split(' ');

  if (nameWords.length === 1) {
    return nameWords[0].slice(0, 3).toUpperCase();
  } else {
    return nameWords.map(word => word.charAt(0)).join('').toUpperCase();
  }
};


export const findPathToFolder = (folders, targetFolderId, path = []) => {
  for (const folder of folders) {
    const currentPath = [...path, folder];
    if (folder.folderId === targetFolderId) return currentPath;

    if (folder?.children?.length) {
      const childPath = findPathToFolder(folder.children, targetFolderId, currentPath);
      if (childPath) return childPath;
    }
  }
  return null;
};

export const findParentFolderRecursive = (folders, targetId) => {
  for (const folder of folders) {
    if (folder.folderId === targetId) return folder;

    if (Array.isArray(folder.children)) {
      const isDirectChild = folder.children.some(child => child.folderId === targetId);
      if (isDirectChild) return folder;

      const result = findParentFolderRecursive(folder.children, targetId);
      if (result) return result;
    }
  }
  return null;
};

export const findFolderById = (folders, targetId) => {
  for (const folder of folders) {
    if (folder.folderId === targetId) {
      return folder; // ✅ Found match
    }

    if (Array.isArray(folder.children)) {
      const result = findFolderById(folder.children, targetId);
      if (result) return result;
    }
  }
  return null; // ❌ Not found
};

export const flattenFolders = (folders) => {
  let result = [];

  for (const folder of folders) {
    result.push(folder); // Include the current folder

    if (Array.isArray(folder.children) && folder.children.length > 0) {
      result = result.concat(flattenFolders(folder.children)); // Recurse children
    }
  }

  return result;
};

export const getFlattenedFoldersWithPaths = (folders, parentPath = []) => {
  let result = [];

  for (const folder of folders) {
    const currentPath = [...parentPath, folder.name]; // Build full path
    result.push({
      label: currentPath.join(" > "),
      value: folder.folderId
    });

    if (Array.isArray(folder.children) && folder.children.length > 0) {
      const children = getFlattenedFoldersWithPaths(folder.children, currentPath);
      result = result.concat(children);
    }
  }

  return result;
};




export const groupByDate = (docs) => {
  const grouped = {};

  docs.forEach(doc => {
    const diff = dayjs().startOf('day').diff(dayjs(doc.updatedAt).startOf('day'), 'day');
    let label = '';

    if (diff === 0) label = 'Today';
    else if (diff === 1) label = 'Yesterday';
    else if (diff <= 7) label = `${diff} days ago`;
    else label = 'Older';

    if (!grouped[label]) grouped[label] = [];
    grouped[label].push(doc);
  });
  const orderedLabels = ['Today', 'Yesterday', '2 days ago', '3 days ago', '4 days ago', '5 days ago', '6 days ago', '7 days ago', 'Older'];
  const orderedGrouped = {};

  orderedLabels.forEach(label => {
    if (grouped[label]) orderedGrouped[label] = grouped[label];
  });

  return orderedGrouped;
};


export const formatDate = (dateStr, format = 'DD-MMM-YYYY hh:mm a', tz) => {
  if (!dateStr || dateStr === '0001-01-01T00:00:00Z') return '';

  const timeZone = tz || dayjs.tz.guess();
  const date = dayjs(dateStr);

  if (!date.isValid()) return '';

  return date.tz(timeZone).format(format).toUpperCase();
};

export const fixSlateDocument = (nodes) => {
  return nodes?.map(node => {
    if (Array.isArray(node.children)) {
      // Fix nested children
      if (node.children.length === 0) {
        node.children = [{ text: '' }];
      } else {
        node.children = fixSlateDocument(node.children);
      }
    }
    return node;
  });
};

export function getCurrentMonthDisplayInfo(inputDate) {
  const date = inputDate ? dayjs(inputDate) : dayjs(); // fallback to today if no date
  const startOfMonth = date.startOf('month');
  const endOfMonth = date.endOf('month');
  const startOfWeek = date.startOf('week');
  const endOfWeek = date.endOf('week');
  const formatCapMonth = (d) =>
    d.format('MMM').charAt(0).toUpperCase() + d.format('MMM').slice(1).toLowerCase();

  return {
    monthShort: formatCapMonth(date),                      // "Jan"
    day: date.format('D'),                                 // "10"
    fullMonthYear: date.format('MMMM YYYY'),               // "January 2025"
    rangeText: `${formatCapMonth(startOfMonth)},${startOfMonth.format('D YYYY')} – ${formatCapMonth(endOfMonth)},${endOfMonth.format('D YYYY')}`,
    weekRangeText: `${formatCapMonth(startOfWeek)} ${startOfWeek.format('D')} – ${formatCapMonth(endOfWeek)} ${endOfWeek.format('D, YYYY')}` // "Jul 8 – Jul 14, 2025"

  };
}

export const generateAllTimes = () => {
  const times = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const time = dayjs().hour(h).minute(m).second(0);
      times.push({
        label: time.format('h:mm a'),
        value: time.format('HH:mm')
      });
    }
  }
  return times;
};

export const generateEndTimes = (startTime) => {
  const allTimes = generateAllTimes();
  const startIndex = allTimes.findIndex(t => t.value === startTime);
  return allTimes.slice(startIndex + 1); // +1 to avoid selecting the same as start
};

export const getOverlappingGroups=(events)=> {
  const groups = [];
  let currentGroup = [];

  events
    .sort((a, b) => dayjs.utc(a.startTime).diff(dayjs.utc(b.startTime)))
    .forEach(event => {
      const start = dayjs.utc(event.startTime);
      // const end = dayjs.utc(event.endTime);

      if (
        currentGroup.length === 0 ||
        start.isBefore(dayjs.utc(currentGroup[currentGroup.length - 1].endTime))
      ) {
        currentGroup.push(event);
      } else {
        groups.push([...currentGroup]);
        currentGroup = [event];
      }
    });

  if (currentGroup.length > 0) groups.push(currentGroup);

  return groups;
};

export const assignColumnsToGroup=(group)=> {
  const columns = [];

  group.forEach(event => {
    const start = dayjs.utc(event.startTime);
    const end = dayjs.utc(event.endTime);

    let placed = false;

    for (let col = 0; col < columns.length; col++) {
      const colEvents = columns[col];
      if (!colEvents.some(e => dayjs.utc(e.endTime).isAfter(start) && dayjs.utc(e.startTime).isBefore(end))) {
        columns[col].push(event);
        event.column = col;
        placed = true;
        break;
      }
    }

    if (!placed) {
      columns.push([event]);
      event.column = columns.length - 1;
    }
  });

  group.forEach(event => {
    event.totalColumns = columns.length;
  });

  return group;
};


const timeDurations = Array.from({ length: 16 }, (_, i) => (i + 1) * 30); // [30, 60, ..., 480]

export const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let min = 0; min < 60; min += 30) {
      const start = dayjs().startOf('day').add(hour, 'hour').add(min, 'minute');
      timeDurations.forEach((dur) => {
        const end = start.add(dur, 'minute');
        if (end.isBefore(dayjs().endOf('day').add(1, 'minute'))) {
          slots.push({
            label: `${start.format('h:mma')} - ${end.format('h:mma')} (${dayjs.duration(dur, 'minutes').humanize()})`,
            value: `${start.format('HH:mm')}-${end.format('HH:mm')}`
          });
        }
      });
    }
  }
  return slots;
};


function parseTime(str) {
  const [h, m] = str.split(':').map(Number);
  return h * 60 + m;
}

function formatTime(minutes) {
  const h = String(Math.floor(minutes / 60)).padStart(2, '0');
  const m = String(minutes % 60).padStart(2, '0');
  return `${h}:${m}`;
}

function getDailyFreeSlotsMap(availableSlots) {
  const dailySlotSets = [];

  for (const day in availableSlots) {
    const slotMap = new Map();

    for (const { slot, booked } of availableSlots[day]) {
      const match = slot.match(/start:(\d{2}:\d{2})\s*,\s*end:(\d{2}:\d{2})/);
      if (!match || booked) continue;

      const [, start, end] = match;
      slotMap.set(start, end);
    }

    dailySlotSets.push(slotMap);
  }

  return dailySlotSets;
}

function intersectSlotMaps(dailySlotMaps) {
  if (dailySlotMaps.length === 0) return new Map();

  const commonSlots = new Map();

  for (const [start, end] of dailySlotMaps[0]) {
    const isInAll = dailySlotMaps.every(map => map.get(start) === end);
    if (isInAll) {
      commonSlots.set(start, end);
    }
  }

  return commonSlots;
}

function buildDurations(slotMap) {
  const durations = Array.from({ length: 16 }, (_, i) => (i + 1) * 30); // 30 mins to 8 hrs
  const results = [];

  const startTimes = Array.from(slotMap.keys()).sort((a, b) => parseTime(a) - parseTime(b));

  for (const start of startTimes) {
    const startMin = parseTime(start);
    for (const duration of durations) {
      let currentMin = startMin;
      let valid = true;

      for (let i = 0; i < duration; i += 30) {
        const current = formatTime(currentMin);
        const next = formatTime(currentMin + 30);
        if (slotMap.get(current) !== next) {
          valid = false;
          break;
        }
        currentMin += 30;
      }

      if (valid) {
        const end = formatTime(startMin + duration);
        const hours = duration / 60;
        const label = `${start} - ${hours < 1 ? duration + ' Mins' : Number.isInteger(hours) ? `${hours} hour` : `${hours} hours`}`;
        results.push({ label, value: `${start}-${end}` });
      } else {
        break; // can't extend further
      }
    }
  }

  return results;
}

// Usage
export const getCommonAvailableTimeBlocks=(availableSlots)=> {
  const dailySlotMaps = getDailyFreeSlotsMap(availableSlots);
  const commonSlotMap = intersectSlotMaps(dailySlotMaps);
  return buildDurations(commonSlotMap);
};


export const DURATIONSLIST=[
  {
    "label": "30 Mins",
    "value": "30 Mins"
  },
  {
    "label": "1 hour",
    "value": "1 hour"
  },
  {
    "label": "1.5 hours",
    "value": "1.5 hours"
  },
  {
    "label": "2 hour",
    "value": "2 hour"
  },
  {
    "label": "2.5 hours",
    "value": "2.5 hours"
  },
  {
    "label": "3 hour",
    "value": "3 hour"
  },
  {
    "label": "3.5 hours",
    "value": "3.5 hours"
  },
  {
    "label": "4 hour",
    "value": "4 hour"
  },
  {
    "label": "4.5 hours",
    "value": "4.5 hours"
  },
  {
    "label": "5 hour",
    "value": "5 hour"
  },
  {
    "label": "5.5 hours",
    "value": "5.5 hours"
  },
  {
    "label": "6 hour",
    "value": "6 hour"
  },
  {
    "label": "6.5 hours",
    "value": "6.5 hours"
  },
  {
    "label": "7 hour",
    "value": "7 hour"
  },
  {
    "label": "7.5 hours",
    "value": "7.5 hours"
  },
  {
    "label": "8 hour",
    "value": "8 hour"
  },
  {
    "label": "8.5 hours",
    "value": "8.5 hours"
  },
  {
    "label": "9 hour",
    "value": "9 hour"
  },
  {
    "label": "9.5 hours",
    "value": "9.5 hours"
  },
  {
    "label": "10 hour",
    "value": "10 hour"
  },
  {
    "label": "10.5 hours",
    "value": "10.5 hours"
  },
  {
    "label": "11 hour",
    "value": "11 hour"
  },
  {
    "label": "11.5 hours",
    "value": "11.5 hours"
  },
  {
    "label": "12 hour",
    "value": "12 hour"
  }
];

export const convertToAmPm=(time24)=> {
  if (!time24) return '';
  const [hourStr, minute] = time24.split(':');
  if (hourStr === undefined || minute === undefined) return time24; // fallback
  let hour = parseInt(hourStr, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12;
  if (hour === 0) hour = 12;
  return `${hour}:${minute} ${ampm}`;
};

export const calculateOverallRatings=(list=[])=> {
  return list.map(item => {
    const values = Object.values(item).map(v => Number(v) || 0);
    const total = values.reduce((sum, v) => sum + v, 0);
    const count = values.length || 1;
    const overallRating = Number((total / count).toFixed(2));
    return overallRating;
  });
};

export const identifyEmailOrMobile = (value) => {
  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const isMobile = /^\d+$/.test(value);

  if (isEmail) return {  email: value };
  if (isMobile) return { mobile: value };
  return { type: 'unknown', value };
};
